// OrganizationMainFields.js

import { Box, CircularProgress, IconButton, Paper, Switch, TextField, Tooltip } from '@mui/material';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { Edit, Save } from '@mui/icons-material';

import { enableDisableOrganization, updateOrganization, uploadOrganizationImage, downloadOrganizationImage, fetchOrganizationDataByUUID } from '../../../services/b2b-users-apiService';
import { LoginContext } from '../../../App';
import CustomButton from '../../b2b-common/CustomButton';

import './OrganizationMainFields.css';

const OrganizationMainFields = ({ organization, onUpdateOrganization }) => {
    const fileInputRef = useRef(null);

    const { loginData } = useContext(LoginContext);

    const [loadingSaveOrganizationInfo, setLoadingSaveOrganizationInfo] = useState(false);
    const [loadingEnableDisableOrganization, setLoadingEnableDisableOrganization] = useState(false);
    const [editingImage, setEditingImage] = useState(false);

    const [currentOrganization, setCurrentOrganization] = useState(organization);
    const [orgImageData, setOrgImageData] = useState(null);
    const [fetchingOrgImage, setFetchingOrgImage] = useState(false);

    const fetchOrganizationImage = async (organization, accessToken) => {
        try {
            setFetchingOrgImage(true);
            const orgImage = await downloadOrganizationImage(organization.organizationUUID, accessToken);
            setOrgImageData(orgImage);
        } catch (error) {
            console.error('Failed to download organization image:', error.message);
        } finally {
            setFetchingOrgImage(false);
        }
    };

    useEffect(() => {
        const { accessToken } = loginData;
        if (accessToken && !orgImageData) {
            fetchOrganizationImage(organization, accessToken);
        }
        setCurrentOrganization(organization);
    }, [organization]);

    const handleSaveOrganizationInfo = async () => {
        try {
            const { accessToken } = loginData;
            setLoadingSaveOrganizationInfo(true);
            const updatedOrg = await updateOrganization(currentOrganization.organizationUUID, currentOrganization, accessToken);
            setCurrentOrganization(prevOrganization => ({ ...prevOrganization, organizationName: updatedOrg.organizationName }));
            onUpdateOrganization(currentOrganization);
        } catch (error) {
            console.error('Failed to update organization:', error.message);
            console.log(error);
        } finally {
            setLoadingSaveOrganizationInfo(false)
        }
    };

    const handleSwitchToggle = async (e) => {
        try {
            const { accessToken } = loginData;
            setLoadingEnableDisableOrganization(true);
            const updatedOrg = await enableDisableOrganization(currentOrganization.organizationUUID, e.target.checked, accessToken);
            setCurrentOrganization(prevOrganization => ({
                ...prevOrganization,
                organizationEnabled: updatedOrg.organizationEnabled,
            }));
            onUpdateOrganization({ ...currentOrganization, organizationEnabled: updatedOrg.organizationEnabled });
        } catch (error) {
            console.error('Failed to toggle organization:', error.message);
            alert('Failed to toggle organization. Please try again.');
        } finally {
            setLoadingEnableDisableOrganization(false);
        }
    };

    const handleOrganizationNameChange = (e) => {
        setCurrentOrganization(prevOrganization => ({ ...prevOrganization, organizationName: e.target.value }));
    };

    const handleEditImage = () => {
        setEditingImage(true);
        fileInputRef.current.click();
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        try {
            const { accessToken } = loginData;
            await uploadOrganizationImage(currentOrganization.organizationUUID, file, accessToken);
            const updatedOrg = await fetchOrganizationDataByUUID(currentOrganization.organizationUUID, accessToken);
            await fetchOrganizationImage(organization, accessToken);
            onUpdateOrganization(updatedOrg);
        } catch (error) {
            console.error('Failed to upload organization image:', error.message);
            alert('Failed to upload organization image. Please try again.');
        } finally {
            setEditingImage(false);
        }
    };

    return (
        <Box className="organization-info">
            <Paper elevation={2} className='organization-info-paper'>
                <div className="organization-image-container">
                    {organization.imagePath && (
                        <>
                            {fetchingOrgImage ? (
                                <CircularProgress size={20} />
                            ) : (
                                <>
                                    <img
                                        src={orgImageData}
                                        alt={`Logo of ${organization.organizationName}`}
                                        className={`organization-image ${editingImage ? 'editing' : ''}`}
                                    />
                                    <IconButton className="edit-icon" onClick={handleEditImage}>
                                        <Edit />
                                    </IconButton>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        style={{ display: 'none' }}
                                        onChange={handleImageUpload}
                                    />
                                </>
                            )
                            }
                        </>
                    )}
                </div>
                <TextField
                    required
                    label="Organization Name"
                    variant="standard"
                    color="primary"
                    value={currentOrganization.organizationName}
                    className="organization-name-textfield"
                    onChange={handleOrganizationNameChange}
                />
                <div className="organization-enabled-switch">
                    <Tooltip title={`Is organization enabled?`} arrow placement="right">
                        <div>
                            {loadingEnableDisableOrganization ? (
                                <CircularProgress size={20} />
                            ) :
                                <Switch
                                    checked={organization.organizationEnabled}
                                    onChange={handleSwitchToggle}
                                    color="primary"
                                    inputProps={{ 'aria-label': 'toggle organization enabled' }}
                                />
                            }
                        </div>
                    </Tooltip>
                </div>
                <div className="organizationActionButtons">

                    <CustomButton label="Save" icon={<Save />} variant="contained"
                        color="primary" key="1"
                        onClick={handleSaveOrganizationInfo}
                        disabled={editingImage}
                        isLoading={loadingSaveOrganizationInfo}
                    />

                </div>
            </Paper>
        </Box>
    );
};

export default OrganizationMainFields;